import { useEffect, useState } from "react";
import {
  MapPin, Clock, Instagram, Facebook, ArrowUpRight,
  Waves, Wine, UtensilsCrossed, Quote, Star, Coffee,
} from "lucide-react";
import BrandHeader from "@/components/BrandHeader";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import TarqMenu from "@/components/TarqMenu";
import Marquee from "@/components/Marquee";
import { Reveal } from "@/lib/useReveal";
import { TARQ, IMAGES, waLink } from "@/lib/brand";
import { TARQ_MENU } from "@/lib/tarqMenu";
import { useSEO } from "@/lib/useSEO";
import { useLang } from "@/i18n/LangContext";

export default function Tarquinio() {
  const { t } = useLang();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useSEO({
    title: "Tarquínio Beach Club | Costa da Caparica",
    description:
      "Beach Club, Bar e Cafetaria na Costa da Caparica. Peixe fresco, cocktails ao sunset e esplanada junto ao mar. Reserve já.",
    image: IMAGES.tarqSunset,
    imageAlt: "Tarquínio Beach Club ao pôr do sol — Costa da Caparica",
    type: "restaurant",
    themeColor: "#1B2B3A",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Restaurant",
      name: "Tarquínio Beach Club",
      description:
        "Beach Club, Bar e Cafetaria à beira-mar na Costa da Caparica. Peixe fresco, marisco, cocktails e vista para o Atlântico.",
      servesCuisine: ["Portuguesa", "Mariscos", "Peixe fresco", "Cocktails"],
      priceRange: "€€",
      telephone: "+351917240397",
      image: [IMAGES.tarqHero, IMAGES.tarqFeature, IMAGES.tarqSunset],
      url: typeof window !== "undefined" ? window.location.href : "",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Muralha da Praia, Apoio 14",
        postalCode: "2825-382",
        addressLocality: "Costa da Caparica",
        addressCountry: "PT",
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: 38.6404,
        longitude: -9.2376,
      },
      openingHoursSpecification: [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday",
          ],
          opens: "09:00",
          closes: "23:00",
        },
      ],
      sameAs: [TARQ.instagram, TARQ.facebook],
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.8",
        reviewCount: "1000",
      },
    },
  });

  const message = t("wa.tarq.reserve");
  const brunchMessage = t("wa.brunch.reserve");
  const P = IMAGES.tarqPhotos;

  return (
    <main className="theme-tarq relative min-h-screen bg-[#FBF8F3] text-[#0F2A44] overflow-x-hidden" data-testid="tarquinio-page">
      <BrandHeader theme="tarq" logoSrc={TARQ.logo} name="Tarquínio Beach Club" />

      {/* ============ HERO — imagem real, cropada ao oceano ============ */}
      <section className="relative min-h-[100svh] w-full overflow-hidden bg-[#FBF8F3]">
        <img
          src={P.entrance}
          alt="Tarquínio Beach Club — Costa da Caparica"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover anim-slow-zoom"
          style={{ objectPosition: "75% 55%" }}
        />
        {/* Minimal navy wash bottom-left only */}
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0F2A44]/70 via-[#0F2A44]/30 via-45% to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-72 bg-gradient-to-t from-[#0F2A44]/85 via-[#0F2A44]/40 to-transparent" />
        <div className="absolute inset-0 grain-soft" />

        {/* Vertical label */}
        <div className="hidden md:flex absolute left-8 top-1/2 -translate-y-1/2 items-center pointer-events-none">
          <span className="vertical-label text-white/70 font-sans-tarq">
            Costa da Caparica · Muralha da Praia
          </span>
        </div>

        {/* Hero brand logo — floating, elegant, positioned over the sky */}
        <Reveal variant="in" delay={250}>
          <img
            src={TARQ.logo}
            alt="Tarquínio Beach Club"
            className="hero-brand-logo absolute z-20 pointer-events-none select-none"
          />
        </Reveal>

        <div className="relative z-10 max-w-[1700px] mx-auto px-5 sm:px-8 lg:px-12 pt-32 sm:pt-36 pb-14 sm:pb-20 min-h-[100svh] flex flex-col justify-end">
          <Reveal variant="up" className="max-w-3xl">
            <div className="flex items-center gap-4 text-[#D4734A] text-[10px] tracking-[0.4em] uppercase font-sans-tarq">
              <span className="h-px w-10 bg-[#D4734A]/70 reveal-line is-visible" />
              Beach Club · Bar · Cafetaria
            </div>
            <h1 className="mt-5 font-serif-tarq text-white text-[clamp(3.5rem,12vw,10rem)] leading-[0.88] font-light tracking-[-0.035em] drop-shadow-[0_2px_24px_rgba(15,42,68,0.35)]">
              Tarquínio
              <br />
              <em className="italic text-[#D4734A]">Beach Club.</em>
            </h1>
            <p className="mt-7 text-white/90 text-base sm:text-lg lg:text-xl font-sans-tarq max-w-lg leading-relaxed">
              Sem pressa. Sem filtros. Mesmo à beira-mar.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a
                href={waLink(message)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-reserve-tarq"
                className="btn btn-lg btn-primary-tarq"
              >
                Reservar via WhatsApp
                <ArrowUpRight className="h-4 w-4" strokeWidth={1.75} />
              </a>
              <a
                href="#espaco"
                data-testid="hero-scroll-tarq"
                className="btn btn-md font-sans-tarq border border-white/55 text-white rounded-full px-7 py-3 hover:bg-white/12 hover:border-white transition-all duration-300"
              >
                Ver o espaço
              </a>
            </div>
          </Reveal>

          <div className="mt-14 hidden sm:flex items-end justify-between text-white/65 text-[10px] tracking-[0.4em] uppercase font-sans-tarq">
            <span>Todos os dias · 09:00 — 23:00</span>
            <div className="flex items-center gap-3">
              <span>Vista Atlântico</span>
              <span className="scroll-hint text-white/55" aria-hidden />
            </div>
            <span>1.000+ opiniões</span>
          </div>
        </div>
      </section>

      {/* ============ MARQUEE — navy contrast band ============ */}
      <div className="relative bg-[#1B2B3A] overflow-hidden">
        {/* Decorative top separator */}
        <div className="absolute top-0 inset-x-0 flex items-center justify-center pt-3 pointer-events-none">
          <span className="h-px flex-1 bg-gradient-to-r from-transparent via-[#E88B4F]/40 to-transparent" />
          <span className="px-4 text-[#E88B4F]/70 text-xs">✦</span>
          <span className="h-px flex-1 bg-gradient-to-r from-transparent via-[#E88B4F]/40 to-transparent" />
        </div>
        <div className="py-3" />
        <Marquee
          items={[
            "Mar · Luz · Tempo",
            "Esplanada sobre a areia",
            "Sunset · Brisa atlântica",
            "Peixe do dia · Cocktails",
            "Aberto desde manhã até à noite",
          ]}
          separator="✦"
          textClass="font-serif-tarq italic text-[#D4734A] text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight"
        />
        <div className="py-3" />
        {/* Decorative bottom separator */}
        <div className="absolute bottom-0 inset-x-0 flex items-center justify-center pb-3 pointer-events-none">
          <span className="h-px flex-1 bg-gradient-to-r from-transparent via-[#E88B4F]/40 to-transparent" />
          <span className="px-4 text-[#E88B4F]/70 text-xs">✦</span>
          <span className="h-px flex-1 bg-gradient-to-r from-transparent via-[#E88B4F]/40 to-transparent" />
        </div>
      </div>

      {/* ============ APRESENTAÇÃO — editorial ============ */}
      <section id="espaco" className="relative bg-[#FBF6EC] overflow-hidden">
        {/* Background watermark phrase — ultra subtle */}
        <span
          aria-hidden
          className="hidden lg:block pointer-events-none absolute -left-12 top-1/2 -translate-y-1/2 font-serif-tarq italic text-[#0F2A44]/[0.04] text-[18rem] font-light leading-none select-none whitespace-nowrap"
        >
          sem pressa.
        </span>

        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-24 sm:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24 items-start">
            <Reveal variant="up" className="lg:col-span-5 lg:sticky lg:top-32">
              <span className="text-[10px] tracking-[0.5em] uppercase text-[#E88B4F] font-sans-tarq">
                O Espaço
              </span>
              <h2 className="mt-6 font-serif-tarq text-[#0F2A44] text-5xl sm:text-6xl lg:text-[4.5rem] font-light leading-[0.98] tracking-[-0.03em]">
                Viver a Costa
                <br />
                <em className="italic">com tempo.</em>
              </h2>
              <div className="mt-8 h-px w-16 bg-[#E88B4F] reveal-line is-visible" />
              <p className="mt-10 font-sans-tarq text-[#4A5568] text-base sm:text-lg leading-[1.85] max-w-md">
                No Tarquínio Beach Club, cada momento acontece sem pressa. Entre o
                mar, a luz da Caparica e uma mesa bem posta, o espaço convida a
                ficar, partilhar e aproveitar o dia até ao fim.
              </p>

              {/* 3 bullets — wave glyph */}
              <ul className="mt-9 space-y-3.5 max-w-md">
                {[
                  "Esplanada junto à areia",
                  "Peixe fresco todos os dias",
                  "Aberto desde manhã até à noite",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 font-sans-tarq text-[#0F2A44] text-[15px] leading-relaxed"
                  >
                    <span
                      className="text-[#D4734A] font-serif-tarq text-xl mt-[-2px] leading-none shrink-0"
                      aria-hidden
                    >
                      ≈
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Reveal>

            <div className="lg:col-span-7">
              <Reveal variant="up">
                <div className="relative aspect-[4/5] overflow-hidden rounded-xl shadow-[0_30px_60px_-25px_rgba(15,42,68,0.35)]">
                  <img src={P.interior} alt="Interior Tarquínio" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1800ms] ease-out hover:scale-[1.03]" />
                </div>
              </Reveal>
              <Reveal variant="up" delay={200} className="mt-6 grid grid-cols-2 gap-4 sm:gap-6">
                <div className="relative aspect-[4/5] overflow-hidden rounded-xl shadow-[0_25px_50px_-20px_rgba(15,42,68,0.3)]">
                  <img src={P.entrance} alt="Entrada Tarquínio" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1800ms] ease-out hover:scale-[1.04]" style={{ objectPosition: "30% 65%" }} />
                </div>
                <div className="relative aspect-[4/5] overflow-hidden rounded-xl shadow-[0_25px_50px_-20px_rgba(15,42,68,0.3)]">
                  <img
                    src="https://customer-assets.emergentagent.com/job_two-tables-one-coast/artifacts/cfrwpysw_Casco%20melhorado.png"
                    alt="Casco de sapateira recheado com pão torrado"
                    className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-[1800ms] ease-out hover:scale-[1.04]"
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ============ PILARES ============ */}
      <section className="relative bg-[#F0E8D9] py-24 sm:py-28">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <Reveal variant="up" className="max-w-xl mb-16 sm:mb-20">
            <span className="text-[10px] tracking-[0.5em] uppercase text-[#E88B4F] font-sans-tarq">
              A Experiência
            </span>
            <h2 className="mt-5 font-serif-tarq text-[#0F2A44] text-4xl sm:text-5xl font-light tracking-[-0.025em] leading-[1]">
              Três pilares,
              <br />
              <em className="italic">um mesmo lugar.</em>
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                icon: Waves,
                n: "i.",
                k: "Vista Mar",
                v: "Esplanada junto à areia, ambiente leve e ligação direta ao Atlântico.",
                tagline: "O melhor lugar para ver o dia passar.",
                bg: P.exteriorSunset,
                bgPos: "50% 70%",
              },
              {
                icon: Wine,
                n: "ii.",
                k: "Bar & Cocktails",
                v: "Cocktails, sangrias, vinhos e bebidas para acompanhar o dia e prolongar o final da tarde.",
                tagline: "Sangrias, gin tónicos e clássicos com vista para o mar.",
                bg: P.cocktails,
                bgPos: "50% 50%",
              },
              {
                icon: UtensilsCrossed,
                n: "iii.",
                k: "Cozinha & Petiscos",
                v: "Peixe, marisco, saladas, petiscos e pratos pensados para saborear o momento sem pressa.",
                tagline: "Do mar à grelha. Sempre fresco, sempre no ponto.",
                bg: "/tarq/dourada.jpg",
                bgPos: "50% 50%",
              },
            ].map(({ icon: Ic, n, k, v, tagline, bg, bgPos }, i) => (
              <Reveal
                key={k}
                variant="up"
                delay={i * 140}
                data-testid={`tarq-pilar-${k.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className="group relative overflow-hidden rounded-xl aspect-[4/5] sm:aspect-[3/4] md:aspect-[4/5] min-h-[480px]"
              >
                {/* Background image */}
                <div
                  className="absolute inset-0 bg-cover transition-transform duration-[1800ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]"
                  style={{ backgroundImage: `url(${bg})`, backgroundPosition: bgPos }}
                />
                {/* Navy overlay */}
                <div className="absolute inset-0 bg-[#0F2A44]/65 group-hover:bg-[#0F2A44]/55 transition-colors duration-700" />
                {/* Bottom fade */}
                <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-[#0F2A44] via-[#0F2A44]/70 to-transparent" />

                {/* Content */}
                <div className="relative h-full flex flex-col justify-between p-7 sm:p-9 lg:p-10">
                  <div className="flex items-start justify-between">
                    <span className="font-serif-tarq italic text-[#D4734A] text-5xl leading-none drop-shadow-[0_2px_8px_rgba(0,0,0,0.4)]">
                      {n}
                    </span>
                    <Ic className="h-8 w-8 text-[#F5EFE0]/90 drop-shadow transition-transform duration-700 group-hover:rotate-6" strokeWidth={1.5} />
                  </div>

                  <div>
                    <h3 className="font-serif-tarq italic text-white text-3xl sm:text-4xl font-light tracking-tight drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]">
                      {k}
                    </h3>
                    <p className="mt-3 font-sans-tarq text-[#F5EFE0]/90 text-sm sm:text-base leading-relaxed max-w-xs drop-shadow">
                      {v}
                    </p>
                    <p className="mt-3 font-serif-tarq italic text-[#D4734A] text-sm sm:text-base">
                      {tagline}
                    </p>
                    <div className="mt-5 h-px w-10 bg-[#D4734A]/70 transition-all duration-700 group-hover:w-24 group-hover:bg-[#D4734A]" />
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ BRUNCH SECTION — premium menu cards ============ */}
      <BrunchSection
        t={t}
        waHref={waLink(brunchMessage)}
      />

      {/* ============ MOMENTOS — Da manhã ao pôr do sol (editorial slideshow) ============ */}
      <MomentosSection
        cocktails={P.cocktails}
        interior={P.interiorGolden}
        sunset={P.sunsetSign}
        morning={P.entrance}
      />

      {/* ============ HISTÓRIA — desde 1929 ============ */}
      <section
        id="historia"
        data-testid="tarq-history"
        className="relative py-24 sm:py-36 bg-[#0F2A44] text-[#F5EFE0] overflow-hidden"
      >
        {/* Warm cinematic ambience */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_30%,rgba(212,115,74,0.18),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_80%_80%,rgba(232,176,120,0.10),transparent_55%)]" />

        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          {/* Editorial header */}
          <Reveal variant="up" className="max-w-2xl">
            <span className="text-[10px] tracking-[0.5em] uppercase text-[#D4734A] font-sans-tarq">
              Desde 1929 · Memória da costa
            </span>
            <h2 className="mt-6 font-serif-tarq text-[#F5EFE0] text-5xl sm:text-6xl lg:text-7xl font-light tracking-[-0.03em] leading-[0.95]">
              Quase um século
              <br />
              <em className="italic text-[#D4734A]">à beira-mar.</em>
            </h2>
          </Reveal>

          {/* Two historical photos side-by-side */}
          <div className="mt-16 sm:mt-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <Reveal variant="up" delay={120} className="lg:col-span-7">
              <div className="grid grid-cols-2 gap-4 sm:gap-6">
                <figure className="relative overflow-hidden rounded-md shadow-[0_30px_60px_-25px_rgba(0,0,0,0.55)]">
                  <img
                    src={P.history1929}
                    alt="Tarquínio em 1929 — pescadores na Costa da Caparica"
                    loading="lazy"
                    className="block w-full h-auto object-cover transition-transform duration-[1800ms] ease-out hover:scale-[1.04]"
                  />
                  <figcaption className="absolute bottom-3 left-3 text-[10px] tracking-[0.4em] uppercase text-[#F5EFE0]/85 font-sans-tarq bg-[#0F2A44]/55 backdrop-blur-sm px-2.5 py-1 rounded-sm">
                    1929
                  </figcaption>
                </figure>
                <figure className="relative overflow-hidden rounded-md shadow-[0_30px_60px_-25px_rgba(0,0,0,0.55)] mt-10 sm:mt-16">
                  <img
                    src={P.history1999}
                    alt="Tarquínio Bar em 1999 — primeira fachada"
                    loading="lazy"
                    className="block w-full h-auto object-cover transition-transform duration-[1800ms] ease-out hover:scale-[1.04]"
                  />
                  <figcaption className="absolute bottom-3 left-3 text-[10px] tracking-[0.4em] uppercase text-[#F5EFE0]/85 font-sans-tarq bg-[#0F2A44]/55 backdrop-blur-sm px-2.5 py-1 rounded-sm">
                    1999
                  </figcaption>
                </figure>
              </div>
            </Reveal>

            <Reveal variant="up" delay={220} className="lg:col-span-5">
              <p className="font-serif-tarq italic text-[#F5EFE0] text-2xl sm:text-3xl lg:text-[2rem] leading-[1.35] font-light tracking-[-0.01em]">
                O Tarquínio nasceu em 1929, numa Costa da Caparica ainda
                marcada pelos pescadores, pela areia selvagem e pelos dias
                passados junto ao mar.
              </p>
              <p className="mt-7 text-[#F5EFE0]/80 text-base sm:text-[1.0625rem] leading-[1.85] font-sans-tarq">
                Ao longo de gerações, tornou-se mais do que um restaurante —
                tornou-se parte da memória da costa.
              </p>
              <p className="mt-5 text-[#F5EFE0]/80 text-base sm:text-[1.0625rem] leading-[1.85] font-sans-tarq">
                Entre sunsets, verões, histórias e encontros, o Tarquínio
                continua hoje a ser um lugar onde o tempo abranda e o mar
                continua à mesa.
              </p>
              <div className="mt-10 flex items-center gap-5">
                <span className="h-px w-14 bg-[#D4734A]" />
                <span className="text-[10px] tracking-[0.4em] uppercase text-[#F5EFE0]/55 font-sans-tarq">
                  Tradição · Costa da Caparica
                </span>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ NÚMEROS / CREDIBILIDADE ============ */}
      <section
        id="numeros"
        data-testid="tarq-stats"
        className="relative py-24 sm:py-32 bg-[#1B2B3A] overflow-hidden"
      >
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,115,74,0.10),transparent_60%)]" />
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-6 lg:gap-x-10">
            {[
              { num: "100", label: "Anos de costa" },
              { num: "200+", label: "Lugares sentados" },
              { num: "4.8★", label: "Avaliação Google" },
              { num: "09—23h", label: "Aberto todos os dias" },
            ].map((s, i) => (
              <div
                key={s.label}
                data-testid={`tarq-stat-${i}`}
                className="text-center md:text-left gateway-fade"
                style={{ animationDelay: `${i * 120}ms` }}
              >
                <div className="font-serif-tarq text-[#D4734A] text-[clamp(2.75rem,5.5vw,4.5rem)] font-light leading-[0.95] tracking-[-0.02em]">
                  {s.num}
                </div>
                <div className="mt-3 h-px w-10 bg-[#D4734A]/40 mx-auto md:mx-0" />
                <div className="mt-3 text-[10px] sm:text-[11px] tracking-[0.32em] uppercase text-[#F5EFE0]/75 font-sans-tarq">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ GALERIA — fotos reais de pratos (1 hero + 3 stacked) ============ */}
      <section id="galeria" data-testid="tarq-food-gallery" className="relative py-24 sm:py-32 bg-[#FBF8F3]">
        {/* Soft golden-hour wash */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(232,176,120,0.18),transparent_55%)] pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(167,196,210,0.12),transparent_60%)] pointer-events-none" />

        <div className="relative max-w-[1700px] mx-auto px-5 sm:px-8 lg:px-12">
          {/* HEADER */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14 sm:mb-20 items-end">
            <Reveal variant="up" className="lg:col-span-7">
              <span className="text-[10px] tracking-[0.5em] uppercase text-[#E88B4F] font-sans-tarq">
                Galeria — pratos
              </span>
              <h2 className="mt-5 font-serif-tarq text-[#0F2A44] text-5xl sm:text-6xl lg:text-7xl font-light tracking-[-0.03em] leading-[0.98]">
                Sabor a mar.
                <br />
                <em className="italic">Experiência real.</em>
              </h2>
            </Reveal>
            <Reveal variant="up" delay={150} className="lg:col-span-5 lg:pb-3">
              <div className="h-px w-12 bg-[#E88B4F] mb-6" />
              <p className="font-sans-tarq text-[#4A5568] text-base leading-relaxed max-w-md">
                Peixe fresco, marisco e pratos pensados para dias longos à
                beira-mar.
              </p>
            </Reveal>
          </div>

          {/* GALLERY GRID — 1 hero (left) + 3 stacked (right) on desktop */}
          <div className="grid grid-cols-12 gap-3 sm:gap-4 lg:gap-5 lg:auto-rows-[260px]">
            {/* Hero — Polvo à Lagareiro (has beach background, perfect Ibiza vibe) */}
            <FoodTile
              src="/tarq/polvo.jpg"
              alt="Polvo à Lagareiro com batata a murro e laranja"
              caption="Polvo à Lagareiro"
              kicker="Da grelha · golden hour"
              span="col-span-12 lg:col-span-7 lg:row-span-2"
              aspect="aspect-[4/3] lg:aspect-auto lg:h-full"
              testid="tarq-food-hero"
              delay={0}
            />

            {/* Stack right — Dourada */}
            <FoodTile
              src="/tarq/dourada.jpg"
              alt="Dourada grelhada com salada e batata"
              caption="Dourada Grelhada"
              kicker="Peixe fresco"
              span="col-span-6 lg:col-span-5 lg:row-span-1"
              aspect="aspect-[5/4] lg:aspect-auto lg:h-full"
              testid="tarq-food-1"
              delay={120}
            />

            {/* Stack right — Casco / Sapateira recheada */}
            <FoodTile
              src="/tarq/casco.jpg"
              alt="Casco de sapateira recheado com pão torrado"
              caption="Casco de Sapateira"
              kicker="Marisco · à mesa"
              span="col-span-6 lg:col-span-5 lg:row-span-1"
              aspect="aspect-[5/4] lg:aspect-auto lg:h-full"
              testid="tarq-food-2"
              delay={220}
            />

            {/* Bottom row full — Choco frito */}
            <FoodTile
              src="/tarq/choco.jpg"
              alt="Choco frito com molhos"
              caption="Choco Frito"
              kicker="Sabor do mar · estaladiço"
              span="col-span-12 lg:col-span-12 lg:row-span-1"
              aspect="aspect-[5/4] lg:aspect-auto lg:h-full"
              testid="tarq-food-3"
              delay={320}
            />
          </div>

          {/* Footer caption */}
          <Reveal variant="up" delay={420} className="mt-10 sm:mt-14 flex items-center justify-between gap-4 flex-wrap">
            <span className="font-sans-tarq text-[11px] tracking-[0.4em] uppercase text-[#7A6A54]">
              Costa da Caparica · Muralha da Praia
            </span>
            <span className="font-serif-tarq italic text-[#E88B4F] text-base sm:text-lg">
              — luz, brisa, momentos.
            </span>
          </Reveal>
        </div>
      </section>

      {/* ============ MENU ============ */}
      <section id="menu" className="bg-[#F0E8D9] border-t border-[#0F2A44]/10">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-20 sm:py-28">
          <Reveal variant="up" className="max-w-3xl">
            <span className="text-[10px] tracking-[0.5em] uppercase text-[#E88B4F] font-sans-tarq">
              A Carta
            </span>
            <h2 className="mt-5 font-serif-tarq text-[#0F2A44] text-5xl sm:text-6xl lg:text-7xl font-light tracking-[-0.03em] leading-[1]">
              Menu.
            </h2>
            <p className="mt-6 font-sans-tarq text-[#4A5568] text-base sm:text-lg leading-relaxed max-w-xl">
              Sabores pensados para acompanhar cada momento à beira-mar.
            </p>
          </Reveal>
          <div className="mt-14 sm:mt-20">
            <TarqMenu categories={TARQ_MENU} />
          </div>
        </div>
      </section>

      {/* ============ BAR & COCKTAILS — visual editorial ============ */}
      <section
        id="bar"
        className="relative bg-[#FBF8F3] py-24 sm:py-32 overflow-hidden"
        data-testid="tarq-bar-section"
      >
        {/* Soft golden warmth backdrop */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(232,176,120,0.20),transparent_55%)] pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(167,196,210,0.10),transparent_60%)] pointer-events-none" />

        <div className="relative max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-12">
          {/* Editorial header */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14 sm:mb-20 items-end">
            <Reveal variant="up" className="lg:col-span-7">
              <span className="text-[10px] tracking-[0.5em] uppercase text-[#D4734A] font-sans-tarq">
                Bar · Sunset · Lifestyle
              </span>
              <h2 className="mt-5 font-serif-tarq text-[#0F2A44] text-5xl sm:text-6xl lg:text-7xl font-light leading-[0.98] tracking-[-0.03em]">
                Cocktails
                <br />
                <em className="italic">ao sunset.</em>
              </h2>
            </Reveal>
            <Reveal variant="up" delay={150} className="lg:col-span-5">
              <p className="font-sans-tarq text-[#4A5568] text-base sm:text-lg leading-[1.8] max-w-md lg:ml-auto">
                Sangrias geladas, gin tónicos, frutas frescas e o frescor do
                vinho branco — pensados para acompanhar a luz da Caparica até
                à última brisa do dia.
              </p>
            </Reveal>
          </div>

          {/* Visual diptych — cocktails + sunset */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 sm:gap-8">
            <Reveal variant="in" className="lg:col-span-7">
              <div className="relative aspect-[4/3] sm:aspect-[16/10] overflow-hidden rounded-md shadow-[0_40px_70px_-30px_rgba(15,42,68,0.45)] group">
                <img
                  src={P.cocktails}
                  alt="Cocktails de melancia ao bar do Tarquínio"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[2000ms] ease-out group-hover:scale-[1.05]"
                />
                <div className="absolute bottom-5 left-5 sm:bottom-7 sm:left-7 text-[10px] tracking-[0.4em] uppercase text-white/95 bg-[#0F2A44]/45 backdrop-blur-sm px-3 py-1.5 rounded-sm font-sans-tarq">
                  Sangrias · Gin · Frutas frescas
                </div>
              </div>
            </Reveal>
            <Reveal variant="up" delay={180} className="lg:col-span-5 flex flex-col gap-5 sm:gap-8">
              <div className="relative aspect-[4/5] lg:flex-1 overflow-hidden rounded-md shadow-[0_40px_70px_-30px_rgba(15,42,68,0.45)] group">
                <img
                  src={P.sunsetSign}
                  alt="Pôr do sol no Tarquínio Beach Club"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[2000ms] ease-out group-hover:scale-[1.05]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F2A44]/55 via-transparent to-transparent" />
                <div className="absolute bottom-5 left-5 right-5">
                  <p className="font-serif-tarq italic text-white text-2xl sm:text-3xl leading-tight tracking-[-0.01em]">
                    O fim do dia
                    <br />
                    <em className="not-italic text-[#F5C892]">à mesa.</em>
                  </p>
                </div>
              </div>
            </Reveal>
          </div>

          {/* CTA strip */}
          <Reveal variant="up" delay={240} className="mt-14 sm:mt-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8">
            <div className="max-w-xl">
              <p className="font-serif-tarq italic text-[#0F2A44] text-2xl sm:text-3xl leading-snug tracking-[-0.01em]">
                Jantares de grupo, sunsets de verão e a melhor brisa da costa.
              </p>
              <p className="mt-3 text-[10px] tracking-[0.4em] uppercase text-[#7A6A54] font-sans-tarq">
                Reserve a sua mesa · Costa da Caparica
              </p>
            </div>
            <a
              href={waLink(message)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="tarq-bar-reserve"
              className="btn btn-md btn-primary-tarq w-full sm:w-auto justify-center"
            >
              Reservar via WhatsApp
              <ArrowUpRight className="h-4 w-4" strokeWidth={1.75} />
            </a>
          </Reveal>
        </div>
      </section>

      {/* ============ PROVA SOCIAL ============ */}
      <section
        id="reviews"
        data-testid="tarq-reviews"
        className="relative bg-[#F5EFE0] text-[#0F2A44] py-24 sm:py-32 overflow-hidden"
      >
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,115,74,0.10),transparent_55%)]" />
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <Reveal variant="up" className="max-w-3xl mb-16 sm:mb-20">
            <span className="text-[10px] tracking-[0.5em] uppercase text-[#D4734A] font-sans-tarq">
              Prova social
            </span>
            <h2 className="mt-5 font-serif-tarq text-[#0F2A44] text-4xl sm:text-5xl lg:text-6xl font-light tracking-[-0.025em] leading-[1]">
              O que dizem
              <br />
              <em className="italic text-[#D4734A]">quem nos visita.</em>
            </h2>
            <p className="mt-6 font-sans-tarq text-[#4A5568] text-base">
              Mais de 4.8★ no Google.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {[
              {
                name: "Margarida S.",
                meta: "Lisboa",
                stars: 5,
                text: "Uma tarde perfeita. Peixe do dia estava incrível, o serviço foi atencioso e a vista para o mar não tem preço. Voltamos com certeza.",
              },
              {
                name: "Bruno F.",
                meta: "Almada",
                stars: 5,
                text: "Descobrimos o Tarquínio por acaso e foi a melhor surpresa do verão. Ambiente descontraído, cocktails excelentes e uma esplanada única. Recomendo a toda a gente.",
              },
              {
                name: "Catarina R.",
                meta: "Setúbal",
                stars: 5,
                text: "Almoço de domingo junto ao mar, sem pressa. Os petiscos são generosos e o peixe é mesmo fresco. Este lugar é especial.",
              },
            ].map((r, i) => (
              <Reveal
                key={r.name}
                variant="up"
                delay={i * 140}
                data-testid={`tarq-review-${i}`}
                className="relative rounded-2xl bg-white p-7 sm:p-9 shadow-[0_4px_24px_rgba(27,43,58,0.08)] border border-[#0F2A44]/5 transition-all duration-500 hover:shadow-[0_12px_36px_rgba(27,43,58,0.12)] hover:-translate-y-1"
              >
                <Quote className="h-7 w-7 text-[#D4734A]" strokeWidth={1} />
                <div className="flex items-center gap-1 mt-4 mb-5 text-[#D4734A]">
                  {Array.from({ length: r.stars }).map((_, j) => (
                    <Star key={j} className="h-4 w-4 fill-[#D4734A]" strokeWidth={0} />
                  ))}
                </div>
                <p className="font-sans-tarq text-[#0F2A44]/85 text-[15px] leading-[1.8]">
                  “{r.text}”
                </p>
                <div className="mt-7 pt-5 border-t border-[#0F2A44]/10 flex items-center gap-4">
                  {/* Avatar circle with initial */}
                  <div className="h-11 w-11 rounded-full bg-[#D4734A]/15 flex items-center justify-center text-[#D4734A] font-serif-tarq italic text-xl">
                    {r.name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-serif-tarq italic text-[#0F2A44] text-lg leading-tight">
                      {r.name}
                    </div>
                    <div className="mt-0.5 text-[10px] tracking-[0.3em] uppercase text-[#7A6A54] font-sans-tarq">
                      {r.meta}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ VISITAR ============ */}
      <section id="visitar" className="relative bg-[#F0E8D9]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-24 sm:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-12">
            <Reveal variant="up">
              <span className="text-[10px] tracking-[0.5em] uppercase text-[#E88B4F] font-sans-tarq">
                Visitar
              </span>
              <h2 className="mt-5 font-serif-tarq text-[#0F2A44] text-5xl sm:text-6xl font-light tracking-[-0.03em] leading-[1]">
                À beira-mar.
                <br />
                <em className="italic">Sempre.</em>
              </h2>
            </Reveal>

            <InfoRow icon={MapPin} title="Morada">
              <div className="text-[#0F2A44]">{TARQ.address}</div>
              <div className="mt-3">
                <a
                  href={TARQ.mapsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="tarq-maps-link"
                  className="link-underline inline-flex items-center gap-1 text-[#E88B4F] text-[11px] tracking-[0.32em] uppercase"
                >
                  Abrir no Google Maps <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              </div>
            </InfoRow>

            <InfoRow icon={Clock} title="Horário">
              {TARQ.hours.map((h, i) => (
                <div key={i} className="flex justify-between gap-4 border-b border-[#0F2A44]/15 py-2.5 last:border-0">
                  <span className="text-[#4A5568]">{h.days}</span>
                  <span className="text-[#0F2A44]">{h.time}</span>
                </div>
              ))}
            </InfoRow>

            <InfoRow icon={ArrowUpRight} title="Reservas">
              <a
                href={waLink(message)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="tarq-reserve-contact"
                className="link-underline inline-flex items-center gap-2 text-[#0F2A44]"
              >
                WhatsApp · 917 240 397
              </a>
              <a
                href="tel:+351917240397"
                data-testid="tarq-phone-call"
                className="block mt-2 link-underline text-[#0F2A44]/80 text-[13px]"
              >
                Telefone · +351 917 240 397
              </a>
            </InfoRow>

            <a
              href={waLink(message)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="tarq-reserve-mesa"
              className="btn btn-lg btn-primary-tarq w-full sm:w-auto mt-2"
            >
              Reservar Mesa
              <ArrowUpRight className="h-4 w-4" strokeWidth={1.75} />
            </a>

            <div className="flex items-center gap-6 pt-2">
              <a
                href={TARQ.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="tarq-instagram"
                className="link-underline inline-flex items-center gap-2 text-[#0F2A44]/80 hover:text-[#0F2A44] text-[11px] tracking-[0.28em] uppercase"
              >
                <Instagram className="h-4 w-4" /> Instagram
              </a>
              <a
                href={TARQ.facebook}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="tarq-facebook"
                className="link-underline inline-flex items-center gap-2 text-[#0F2A44]/80 hover:text-[#0F2A44] text-[11px] tracking-[0.28em] uppercase"
              >
                <Facebook className="h-4 w-4" /> Facebook
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <Reveal variant="in" className="relative w-full aspect-[4/3] lg:aspect-[16/13] overflow-hidden border border-[#0F2A44]/15">
              <iframe
                title="Mapa Tarquínio"
                src={TARQ.mapsQuery}
                className="absolute inset-0 w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ CTA FINAL ============ */}
      <section
        data-testid="tarq-cta-final"
        className="relative py-28 sm:py-36 bg-[#0F2A44] overflow-hidden"
      >
        {/* Sunset photo background */}
        <div className="absolute inset-0">
          <img
            src={P.nightExterior}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            aria-hidden
          />
          <div className="absolute inset-0 bg-[#0F2A44]/72" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F2A44] via-[#0F2A44]/60 to-[#0F2A44]/40" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_40%,rgba(212,115,74,0.22),transparent_60%)]" />
        </div>

        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <Reveal variant="up">
            <span className="text-[10px] tracking-[0.5em] uppercase text-[#D4734A] font-sans-tarq">
              A sua mesa espera
            </span>
            <h2 className="mt-6 font-serif-tarq text-white text-[clamp(4rem,11vw,8rem)] font-light tracking-[-0.04em] leading-[0.92]">
              Reserve.
              <br />
              <em className="italic text-[#D4734A]">Depois é só deixar-se ficar.</em>
            </h2>
            <a
              href={waLink(message)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="tarq-final-cta"
              className="btn btn-lg btn-primary-tarq btn-pulse-bbq mt-14"
            >
              Reservar via WhatsApp
              <ArrowUpRight className="h-4 w-4" strokeWidth={1.75} />
            </a>
            <p className="mt-6 text-[11px] tracking-[0.32em] uppercase text-[#F5EFE0]/70 font-sans-tarq">
              Resposta em menos de 5 minutos · Todos os dias · 10h — 23h
            </p>
          </Reveal>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="bg-[#0D1B2A] text-[#F5EFE0]/80 pt-14 pb-24 sm:pb-10 relative" data-testid="tarq-footer">
        {/* Top coral line */}
        <div className="absolute top-0 inset-x-0 h-[3px] bg-[#D4734A]" />

        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 mt-2 grid grid-cols-1 md:grid-cols-3 gap-10 items-start">
          {/* Brand */}
          <div className="flex flex-col items-center md:items-start gap-3 text-center md:text-left">
            <img
              src={TARQ.logo}
              alt="Tarquínio Beach Club"
              className="h-12 w-auto opacity-90"
            />
            <span className="font-serif-tarq italic text-[#F5EFE0] text-xl">
              Tarquínio Beach Club
            </span>
            <span className="text-[10px] tracking-[0.4em] uppercase text-[#F5EFE0]/55 font-sans-tarq">
              Beach Club · Bar · Cafetaria · Costa da Caparica
            </span>
          </div>

          {/* Nav */}
          <nav
            aria-label="Navegação rodapé"
            className="flex flex-col items-center gap-3 text-[11px] tracking-[0.3em] uppercase font-sans-tarq"
          >
            {[
              { href: "#espaco", label: "O Espaço" },
              { href: "#momentos", label: "Momentos" },
              { href: "#galeria", label: "Galeria" },
              { href: "#menu", label: "Menu" },
              { href: "#visitar", label: "Visitar" },
              { href: "/bbq", label: "BBQ Assador" },
            ].map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="link-underline text-[#F5EFE0]/75 hover:text-[#E88B4F] transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>

          {/* Social + meta */}
          <div className="flex flex-col items-center md:items-end gap-4">
            <div className="flex items-center gap-5">
              <a
                href={TARQ.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="text-[#F5EFE0]/70 hover:text-[#E88B4F] transition-colors"
              >
                <Instagram className="h-5 w-5" strokeWidth={1.5} />
              </a>
              <a
                href={TARQ.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="text-[#F5EFE0]/70 hover:text-[#E88B4F] transition-colors"
              >
                <Facebook className="h-5 w-5" strokeWidth={1.5} />
              </a>
            </div>
            <a
              href={waLink(message)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] tracking-[0.32em] uppercase text-[#F5EFE0]/70 hover:text-[#E88B4F] transition-colors font-sans-tarq"
            >
              WhatsApp · 917 240 397
            </a>
          </div>
        </div>

        {/* Bottom legal */}
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 mt-14 pt-6 border-t border-[#F5EFE0]/10 text-center">
          <p
            className="font-sans-tarq"
            style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.35)", letterSpacing: "0.32em", textTransform: "uppercase" }}
          >
            © 2025 Tarquínio Beach Club · Costa da Caparica · Todos os direitos reservados
          </p>
        </div>
      </footer>

      <WhatsAppFloat theme="tarq" label="Reservar" />
    </main>
  );
}

const InfoRow = ({ icon: Icon, title, children }) => (
  <div>
    <div className="flex items-center gap-3 mb-3">
      <Icon className="h-4 w-4 text-[#E88B4F]" strokeWidth={1.5} />
      <span className="text-[10px] tracking-[0.4em] uppercase text-[#7A6A54] font-sans-tarq">
        {title}
      </span>
    </div>
    <div className="font-sans-tarq text-sm sm:text-base text-[#0F2A44] leading-relaxed">
      {children}
    </div>
  </div>
);

/* ============ Food Gallery Tile (premium hover, soft caption) ============ */
const FoodTile = ({ src, alt, caption, kicker, span, aspect, testid, delay = 0 }) => (
  <Reveal
    variant="up"
    delay={delay}
    data-testid={testid}
    className={`relative ${span} ${aspect} overflow-hidden rounded-sm group bg-[#E9DCC6]`}
  >
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1600ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.045]"
    />
    {/* Caption gradient — appears on hover */}
    <div className="absolute inset-0 bg-gradient-to-t from-[#0F2A44]/65 via-[#0F2A44]/0 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
    {/* Caption */}
    <div className="absolute bottom-0 inset-x-0 p-5 sm:p-6 lg:p-7 translate-y-3 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]">
      <span className="text-[9px] sm:text-[10px] tracking-[0.4em] uppercase text-[#F0E8D9]/85 font-sans-tarq drop-shadow">
        {kicker}
      </span>
      <h3 className="mt-1.5 font-serif-tarq italic text-white text-xl sm:text-2xl lg:text-3xl tracking-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)]">
        {caption}
      </h3>
    </div>
  </Reveal>
);


/* ============================================================
   MomentosSection — editorial slideshow "Da manhã ao pôr do sol"
   Auto-rotates 4 atmospheric moments with cross-fade + Ken Burns zoom.
   Clickable timeline lets visitors jump between moments.
   ============================================================ */
function MomentosSection({ cocktails, interior, sunset, morning }) {
  const slides = [
    {
      kicker: "09h · Brunch",
      title: "Brunch à beira-mar",
      sub: "Café, frutas frescas e o som do Atlântico",
      img: morning,
      pos: "30% 65%",
    },
    {
      kicker: "13h · Almoço",
      title: "Mesa posta",
      sub: "Peixe fresco, marisco e vinho gelado",
      img: interior,
      pos: "50% 50%",
    },
    {
      kicker: "18h · Tarde",
      title: "Cocktails ao bar",
      sub: "Frutas frescas, gin e a luz a baixar",
      img: cocktails,
      pos: "50% 50%",
    },
    {
      kicker: "20h · Sunset",
      title: "Sunset dourado",
      sub: "Encontros que se prolongam naturalmente",
      img: sunset,
      pos: "50% 45%",
    },
  ];

  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = setInterval(
      () => setActive((i) => (i + 1) % slides.length),
      5400
    );
    return () => clearInterval(id);
  }, [slides.length]);

  return (
    <section
      id="momentos"
      data-testid="tarq-momentos"
      className="relative py-24 sm:py-32 bg-[#FBF8F3] overflow-hidden"
    >
      {/* Warm editorial ambience */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(232,176,120,0.22),transparent_60%)] pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(167,196,210,0.10),transparent_60%)] pointer-events-none" />

      <div className="relative max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* LEFT — editorial copy + timeline */}
          <Reveal variant="up" className="lg:col-span-5 lg:pr-4">
            <span className="text-[10px] tracking-[0.5em] uppercase text-[#D4734A] font-sans-tarq">
              Momentos
            </span>
            <h2 className="mt-5 font-serif-tarq text-[#0F2A44] text-5xl sm:text-6xl lg:text-7xl font-light leading-[0.95] tracking-[-0.03em]">
              <em className="italic text-[#D4734A]">Brunch.</em>
              <br />
              Mar. Sunset.
            </h2>
            <p className="mt-8 font-sans-tarq text-[#4A5568] text-base sm:text-lg leading-[1.8] max-w-md">
              Do primeiro café da manhã aos últimos raios de sol, o Tarquínio
              acompanha o ritmo da costa — brunches demorados, almoço à
              beira-mar, cocktails frescos e sunsets que pedem para ficar mais
              um pouco.
            </p>

            {/* BRUNCH À BEIRA-MAR — premium signature block */}
            <div
              data-testid="tarq-brunch-badge"
              className="mt-10 relative overflow-hidden rounded-md border border-[#D4734A]/35 bg-gradient-to-br from-[#FFF8EC] via-[#FBF4E6] to-[#F5E9D0] shadow-[0_18px_40px_-22px_rgba(15,42,68,0.32)] max-w-md"
            >
              <span className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_85%_15%,rgba(232,176,120,0.32),transparent_55%)]" />
              <span
                aria-hidden
                className="pointer-events-none absolute -top-6 -right-3 font-serif-tarq italic text-[6rem] sm:text-[7rem] leading-none text-[#D4734A]/[0.10] select-none"
              >
                09h
              </span>
              <div className="relative flex items-start gap-4 sm:gap-5 p-5 sm:p-6">
                <span className="flex items-center justify-center h-11 w-11 sm:h-12 sm:w-12 rounded-full bg-[#0F2A44] text-[#F5C892] shrink-0">
                  <Coffee className="h-5 w-5" strokeWidth={1.5} />
                </span>
                <div className="min-w-0">
                  <div className="text-[10px] tracking-[0.45em] uppercase text-[#D4734A] font-sans-tarq">
                    Assinatura · Beach Club
                  </div>
                  <div className="mt-1.5 font-serif-tarq italic text-[#0F2A44] text-2xl sm:text-[1.75rem] tracking-tight leading-tight">
                    Brunch à beira-mar
                  </div>
                  <div className="mt-1.5 text-[11px] sm:text-xs tracking-[0.18em] text-[#4A5568] font-sans-tarq">
                    Todos os dias desde as 09h · Café, frutas, mar
                  </div>
                </div>
              </div>
            </div>

            {/* Hours block — both venues */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
              <div className="border-l-2 border-[#D4734A] pl-4 sm:pl-5">
                <div className="font-serif-tarq italic text-[#0F2A44] text-lg sm:text-xl tracking-tight">
                  Tarquínio Beach Club
                </div>
                <div className="mt-1 text-[10px] tracking-[0.4em] uppercase text-[#7A6A54] font-sans-tarq">
                  09:00 — 23:00 · Todos os dias
                </div>
              </div>
              <div className="border-l-2 border-[#D4734A] pl-4 sm:pl-5">
                <div className="font-serif-tarq italic text-[#0F2A44] text-lg sm:text-xl tracking-tight">
                  BBQ Restaurante
                </div>
                <div className="mt-1 text-[10px] tracking-[0.4em] uppercase text-[#7A6A54] font-sans-tarq">
                  10:00 — 22:00 · Todos os dias
                </div>
              </div>
            </div>

            {/* Timeline — clickable moments */}
            <div className="mt-12 sm:mt-14 space-y-1">
              {slides.map((s, i) => {
                const isActive = i === active;
                return (
                  <button
                    key={s.kicker}
                    onClick={() => setActive(i)}
                    data-testid={`momentos-step-${i}`}
                    className="group w-full text-left py-3.5 sm:py-4 flex items-center gap-4 sm:gap-5 border-t border-[#0F2A44]/12 transition-all duration-500"
                  >
                    {/* Progress bar */}
                    <span className="relative h-[2px] w-12 sm:w-16 bg-[#0F2A44]/15 overflow-hidden shrink-0">
                      <span
                        className={`absolute inset-y-0 left-0 bg-[#D4734A] transition-[width] ease-linear ${
                          isActive ? "momentos-bar-active" : "w-0"
                        }`}
                      />
                    </span>
                    <span
                      className={`text-[10px] tracking-[0.32em] uppercase font-sans-tarq transition-colors duration-300 ${
                        isActive
                          ? "text-[#D4734A]"
                          : "text-[#7A6A54] group-hover:text-[#0F2A44]"
                      }`}
                    >
                      {s.kicker}
                    </span>
                    <span
                      className={`font-serif-tarq italic text-lg sm:text-xl tracking-tight ml-auto transition-colors duration-300 ${
                        isActive
                          ? "text-[#0F2A44]"
                          : "text-[#0F2A44]/45 group-hover:text-[#0F2A44]/80"
                      }`}
                    >
                      {s.title}
                    </span>
                  </button>
                );
              })}
              <div className="border-t border-[#0F2A44]/12" />
            </div>
          </Reveal>

          {/* RIGHT — cinematic slideshow */}
          <Reveal variant="in" delay={200} className="lg:col-span-7">
            <div className="relative aspect-[4/5] sm:aspect-[3/4] lg:aspect-[4/5] overflow-hidden rounded-md shadow-[0_50px_80px_-30px_rgba(15,42,68,0.5)]">
              {slides.map((s, i) => (
                <img
                  key={s.kicker}
                  src={s.img}
                  alt={s.title}
                  loading={i === 0 ? "eager" : "lazy"}
                  className={`momentos-slide absolute inset-0 w-full h-full object-cover transition-opacity duration-[1400ms] ease-out ${
                    i === active ? "opacity-100" : "opacity-0"
                  }`}
                  style={{
                    objectPosition: s.pos,
                    animationPlayState: i === active ? "running" : "paused",
                  }}
                />
              ))}

              {/* Caption gradient + caption */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F2A44]/82 via-[#0F2A44]/15 to-transparent pointer-events-none" />
              <div className="absolute inset-x-0 bottom-0 p-6 sm:p-9 lg:p-12 pointer-events-none">
                <span className="text-[10px] sm:text-[11px] tracking-[0.42em] uppercase text-[#F5C892] font-sans-tarq">
                  {slides[active].kicker}
                </span>
                <h3 className="mt-3 font-serif-tarq text-white text-3xl sm:text-4xl lg:text-5xl font-light tracking-[-0.02em] leading-[1.05]">
                  {slides[active].title}
                </h3>
                <p className="mt-3 max-w-sm font-sans-tarq text-white/85 text-sm sm:text-base leading-relaxed">
                  {slides[active].sub}
                </p>
              </div>

              {/* Counter top-right */}
              <div className="absolute top-5 right-5 sm:top-7 sm:right-7 text-[10px] tracking-[0.4em] uppercase text-white/85 font-sans-tarq bg-[#0F2A44]/35 backdrop-blur-sm px-3 py-1.5 rounded-sm">
                {String(active + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}


/* ============================================================
   BrunchSection — premium menu cards for the Tarquínio brunch.
   4 individual cards (Açaí, Avocado Toast, Brunch Menu 1 & 2)
   each with its own real product photo, price and description.
   Light, luminous, beach-club aesthetic — fully translatable.
   ============================================================ */
function BrunchSection({ t, waHref }) {
  const items = [
    {
      key: "acai",
      img: "https://customer-assets.emergentagent.com/job_two-tables-one-coast/artifacts/xtww8cit_BB5FF4EB-8608-487C-AB8B-CC31D3F9B7BB.png",
      alt: "Açaí com toppings frescos",
      price: "6,90€",
      tag: "Copo",
    },
    {
      key: "abacate",
      img: "https://customer-assets.emergentagent.com/job_two-tables-one-coast/artifacts/py5jsc21_BB379658-DB69-4449-BF3C-95755663308A.png",
      alt: "Torrada com creme de abacate e tomate fresco",
      price: "6,90€",
      tag: "Torrada",
    },
    {
      key: "menu1",
      img: "https://customer-assets.emergentagent.com/job_two-tables-one-coast/artifacts/jhnphcjk_0C9A5795-FFB3-4400-AB89-462CB59F38E4.png",
      alt: "Menu Nº1 — ovo mexido, sumo de laranja, torrada e café",
      price: "9,90€",
      tag: "Nº1",
    },
    {
      key: "menu2",
      img: "https://customer-assets.emergentagent.com/job_two-tables-one-coast/artifacts/iyoluh83_E82578D7-7620-4BFB-8471-0B3E574D0FD3.png",
      alt: "Menu Nº2 — torrada doce, iogurte, 3 macarons e americano",
      price: "9,90€",
      tag: "Nº2",
    },
  ];

  return (
    <section
      id="brunch"
      data-testid="tarq-brunch-section"
      className="relative py-24 sm:py-32 bg-gradient-to-b from-[#FBF8F3] via-[#F5EFE0] to-[#FBF8F3] overflow-hidden"
    >
      {/* warm light ambience */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(232,176,120,0.20),transparent_60%)] pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(167,196,210,0.12),transparent_60%)] pointer-events-none" />

      <div className="relative max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-12">
        {/* Editorial header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14 sm:mb-20 items-end">
          <Reveal variant="up" className="lg:col-span-7">
            <span className="text-[10px] tracking-[0.5em] uppercase text-[#D4734A] font-sans-tarq">
              {t("brunch.kicker")}
            </span>
            <h2 className="mt-5 font-serif-tarq text-[#0F2A44] text-5xl sm:text-6xl lg:text-7xl font-light leading-[0.95] tracking-[-0.03em]">
              <em className="italic text-[#D4734A]">{t("brunch.title")}</em>
            </h2>
            <p className="mt-7 font-sans-tarq text-[#4A5568] text-base sm:text-lg leading-[1.8] max-w-xl">
              {t("brunch.sub")}
            </p>
          </Reveal>
          <Reveal variant="up" delay={150} className="lg:col-span-5 lg:text-right">
            <span className="inline-flex items-center gap-3 rounded-full border border-[#D4734A]/35 bg-white/70 backdrop-blur-sm px-5 py-2.5 shadow-[0_10px_24px_-14px_rgba(212,115,74,0.4)]">
              <span className="h-2 w-2 rounded-full bg-[#D4734A] animate-pulse" />
              <span className="text-[10px] tracking-[0.4em] uppercase text-[#0F2A44] font-sans-tarq font-medium">
                {t("brunch.available")}
              </span>
            </span>
          </Reveal>
        </div>

        {/* 4 cards — flyer-style (image speaks for itself: nome+toppings+preço já dentro) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {items.map((it, i) => (
            <Reveal
              key={it.key}
              variant="up"
              delay={i * 120}
              data-testid={`brunch-card-${it.key}`}
              className="group relative rounded-md overflow-hidden shadow-[0_18px_40px_-22px_rgba(15,42,68,0.28)] hover:shadow-[0_28px_55px_-22px_rgba(15,42,68,0.42)] transition-all duration-500 bg-white"
            >
              <div className="relative aspect-square overflow-hidden bg-[#FFFFFF]">
                <img
                  src={it.img}
                  alt={it.alt}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-contain transition-transform duration-[1500ms] ease-out group-hover:scale-[1.04]"
                />
              </div>
            </Reveal>
          ))}
        </div>

        {/* CTA — reserve brunch */}
        <Reveal variant="up" delay={500} className="mt-14 sm:mt-16 flex justify-center">
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="brunch-reserve"
            className="btn btn-lg btn-primary-tarq"
          >
            {t("btn.reserveBrunch")}
            <ArrowUpRight className="h-4 w-4" strokeWidth={1.75} />
          </a>
        </Reveal>
      </div>
    </section>
  );
}
